import React, { useState, useMemo, useEffect, useRef } from "react";
import styled from "styled-components";

const Wrapper = styled.div<{
	$allowOverflow?: TextSelect.Props["allowOverflow"];
}>`
	position: relative;
	width: 300px;

	${({ $allowOverflow }) => ($allowOverflow ? "overflow: visible;" : "")}
`;

const Input = styled.input<{ $error?: boolean }>`
	width: 100%;
	padding: 6px 12px;
	border: 1px solid ${({ $error }) => ($error ? "red" : "#ccc")};
	border-radius: 4px;
	outline: none;
`;

const Dropdown = styled.ul<{ $allowOverflow?: boolean }>`
	position: absolute;
	top: 100%;
	left: 0;
	${({ $allowOverflow }) =>
		$allowOverflow
			? `
				min-width: 100%;
				white-space: nowrap;
        overflow-x: hidden;
			`
			: `
				right: 0; 
			`};

	max-height: 200px;
	overflow-y: auto;
	margin: 0;
	padding: 4px 0;
	list-style: none;
	background: white;
	border: 1px solid #ccc;
	border-top: none;
	z-index: 1000;
`;

const OptionItem = styled.li<{
	$active?: boolean;
	$allowOverflow?: TextSelect.Props["allowOverflow"];
}>`
	padding: 6px 12px;
	cursor: pointer;
	background: ${({ $active }) => ($active ? "#e6f7ff" : "white")};

	&:hover {
		background: #f0f0f0;
	}

	${({ $allowOverflow }) =>
		$allowOverflow
			? `
				width: max-content;
				min-width: 100%;
				overflow: visible;
				white-space: nowrap;
			`
			: ""}
`;

const TextSelect: React.FC<TextSelect.Props> = ({
	value,
	options = [],
	placeholder,
	error,
	limit,
	onChange,
	onSelect,
	onFocus,
	onBlur,
	autoFilter = false,
	allowOverflow = false,
	idInput,
}) => {
	const [inputValue, setInputValue] = useState(value ?? "");
	const [focused, setFocused] = useState(false);
	const [activeIndex, setActiveIndex] = useState<number>(-1);
	const dropdownRef = useRef<HTMLUListElement>(null);

	useEffect(() => {
		setInputValue(value ?? "");
	}, [value]);

	const filteredOptions = useMemo(() => {
		const optionsFiltered = autoFilter
			? options.filter((opt) =>
					opt.label.toLowerCase().includes(inputValue.toLowerCase()),
			  )
			: options;
		const retval = limit
			? optionsFiltered.slice(0, limit)
			: optionsFiltered;

		return retval;
	}, [options, autoFilter, limit, inputValue]);

	const handleSelect = (option: TextSelect.Option<any>) => {
		setInputValue(option.label);
		onChange?.(option.label);
		onSelect?.(option);
		setFocused(false);
		setActiveIndex(-1);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!focused || filteredOptions.length === 0) return;

		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				setActiveIndex((prev) =>
					prev < filteredOptions.length - 1 ? prev + 1 : 0,
				);
				break;
			case "ArrowUp":
				e.preventDefault();
				setActiveIndex((prev) =>
					prev > 0 ? prev - 1 : filteredOptions.length - 1,
				);
				break;
			case "Enter":
				e.preventDefault();
				if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
					handleSelect(filteredOptions[activeIndex]);
				}
				break;
			case "Escape":
				setFocused(false);
				break;
			default:
				break;
		}
	};

	return (
		<Wrapper $allowOverflow={allowOverflow}>
			<Input
				id={idInput}
				$error={error}
				type="text"
				value={inputValue}
				placeholder={placeholder}
				onChange={(e) => {
					setInputValue(e.target.value);
					onChange?.(e.target.value);
					setActiveIndex(0);
				}}
				onFocus={(e) => {
					setFocused(true);
					onFocus?.(e);
				}}
				onBlur={(e) => {
					setTimeout(() => setFocused(false), 200);
					onBlur?.(e);
				}}
				onKeyDown={handleKeyDown}
			/>
			{focused && filteredOptions.length > 0 && (
				<Dropdown ref={dropdownRef} $allowOverflow={allowOverflow}>
					{filteredOptions.map((opt, index) => (
						<OptionItem
							key={opt.key}
							$active={index === activeIndex}
							$allowOverflow={allowOverflow}
							onMouseDown={() => handleSelect(opt)}
						>
							{opt.component ?? opt.label}
						</OptionItem>
					))}
				</Dropdown>
			)}
		</Wrapper>
	);
};

declare namespace TextSelect {
	export interface Props {
		value?: string;
		options?: Option[];
		placeholder?: string;
		error?: boolean;
		limit?: number;
		onChange?: (value: string) => void;
		onSelect?: (option: Option) => void;
		onFocus?: React.FocusEventHandler<HTMLInputElement>;
		onBlur?: React.FocusEventHandler<HTMLInputElement>;
		autoFilter?: boolean;
		allowOverflow?: boolean;
		idInput?: string;
	}

	export interface Option<T = any> {
		key: string | number;
		label: string;
		value: T;
		component?: React.ReactNode;
	}
}

export default TextSelect;
